import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';

type Props = {
  sessionId?: string;
  documentId?: string;
  format?: string;
  templateId?: string;
  without_speakers?: boolean;
  without_timestamps?: boolean;
  token?: string; // For shared document downloads
  isShared?: boolean; // Explicitly indicates this is a shared document context
  send_to_email?: boolean; // Send export to user's email (for template exports)
};

type DirectDownloadResponse = {
  result: {
    download_url: string;
    expires_in: number;
    filename: string;
    content_type: string;
    size_bytes?: number;
  };
};

/**
 * Get direct download URL from Azure Blob Storage (bypasses load balancer)
 *
 * COST OPTIMIZATION: This saves €400-500/month by not streaming files through
 * the API infrastructure. Instead, the API returns a SAS URL that allows the
 * browser to download directly from Azure Blob Storage.
 *
 * Features:
 * - Uses pre-generated files when available (no JSON read required)
 * - Falls back to on-demand generation if pre-generated file missing
 * - Returns time-limited SAS URL (1 hour expiry)
 * - Supports all formats: PDF, DOCX, HTML, TXT, SRT, VTT, SBV, TTML
 *
 * @param props - Document download parameters
 * @returns Promise with download URL and metadata
 */
export async function getDocumentDirectUrl({
  sessionId,
  documentId,
  format,
  templateId,
  without_speakers,
  without_timestamps,
  token,
  isShared,
  send_to_email
}: Props): Promise<DirectDownloadResponse> {
  // Use sharedDocument endpoint when isShared is true OR token is provided (magic link access)
  // Otherwise use regular document endpoint (authenticated access)
  const endpoints = GET_URL(sessionId, documentId);
  const baseUrl =
    isShared || token ? endpoints.sharedDocument : endpoints.document;

  // Add direct_download=true to get SAS URL instead of file blob
  const response = await Axios.get<DirectDownloadResponse>(baseUrl, {
    params: {
      without_speakers,
      without_timestamps,
      direct_download: true,
      ...(format && { format }), // Add format for shared endpoint (query param)
      ...(templateId && { template_id: templateId }), // Add template_id for shared endpoint
      ...(token && { token }), // Add token for shared document downloads
      ...(send_to_email !== undefined && { send_to_email }) // Send to email for template exports
    }
  });

  return response.data;
}

/**
 * Trigger a file download from a URL by fetching into a blob and using a
 * programmatic <a download> click. This works reliably regardless of the
 * server's Content-Disposition / Content-Type headers and avoids navigating
 * away from the SPA.
 */
export async function triggerDownloadFromUrl(
  url: string,
  filename: string,
  contentType?: string
): Promise<void> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  const blob = await response.blob();
  const typed = contentType ? new Blob([blob], { type: contentType }) : blob;
  const objectUrl = window.URL.createObjectURL(typed);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Download document directly from Azure Blob Storage
 *
 * This function gets the SAS URL and triggers the browser to download
 * the file directly from Azure Blobs, bypassing the API infrastructure.
 *
 * @param props - Document download parameters
 */
export async function downloadDocumentDirect(props: Props): Promise<void> {
  const response = await getDocumentDirectUrl(props);
  await triggerDownloadFromUrl(
    response.result.download_url,
    response.result.filename,
    response.result.content_type
  );
}
